import { Command } from 'commander'
import { readFileSync } from 'node:fs'
import { fileURLToPath } from 'node:url'
import { dirname, join } from 'node:path'
import pc from 'picocolors'

// Import proxy for ccusage
import { executeCcusage } from './ccusage-proxy.js'
import { getApiKey } from './config.js'

// Import vibetime-specific commands
import { syncCommand } from './commands/sync.js'
import { configCommand } from './commands/config.js'
import { testCommand } from './commands/test.js'

const __filename = fileURLToPath(import.meta.url)
const __dirname = dirname(__filename)

// Read package.json for version
const packageJson = JSON.parse(
  readFileSync(join(__dirname, '..', 'package.json'), 'utf-8')
)

const program = new Command()

program
  .name('vibetime')
  .description('Extended CLI tool for Claude API usage tracking and ranking\nFully compatible with ccusage commands')
  .version(packageJson.version)

// Add ccusage-compatible commands with direct proxy
program
  .command('daily')
  .description('View daily Claude usage report (ccusage compatible)')
  .option('-s, --since <since>', 'Filter from date (YYYYMMDD format)')
  .option('-u, --until <until>', 'Filter until date (YYYYMMDD format)')
  .option('-j, --json', 'Output in JSON format')
  .option('-m, --mode <mode>', 'Cost calculation mode: auto (use costUSD if exists, otherwise calculate), calculate (always calculate), display (always use costUSD)', 'auto')
  .option('-d, --debug', 'Show pricing mismatch information for debugging')
  .option('--debug-samples <samples>', 'Number of sample discrepancies to show in debug output', '5')
  .option('-o, --order <order>', 'Sort order: desc (newest first) or asc (oldest first)', 'asc')
  .option('-b, --breakdown', 'Show per-model cost breakdown')
  .option('-O, --offline', 'Use cached pricing data for Claude models instead of fetching from API')
  .option('--no-offline', 'Use online pricing data')
  .option('--color', 'Enable colored output (default: auto). FORCE_COLOR=1 has the same effect.')
  .option('--no-color', 'Disable colored output (default: auto). NO_COLOR=1 has the same effect.')
  .option('-i, --instances', 'Show usage breakdown by project/instance')
  .option('-p, --project <project>', 'Filter to specific project name')
  .action(async () => {
    const args = process.argv.slice(3) // Remove 'vibetime', 'daily'
    const result = await executeCcusage(['daily', ...args])
    process.exit(result.exitCode)
  })

program
  .command('monthly')
  .description('View monthly Claude usage report (ccusage compatible)')
  .option('-s, --since <since>', 'Filter from date (YYYYMMDD format)')
  .option('-u, --until <until>', 'Filter until date (YYYYMMDD format)')
  .option('-j, --json', 'Output in JSON format')
  .option('-m, --mode <mode>', 'Cost calculation mode: auto (use costUSD if exists, otherwise calculate), calculate (always calculate), display (always use costUSD)', 'auto')
  .option('-d, --debug', 'Show pricing mismatch information for debugging')
  .option('--debug-samples <samples>', 'Number of sample discrepancies to show in debug output', '5')
  .option('-o, --order <order>', 'Sort order: desc (newest first) or asc (oldest first)', 'asc')
  .option('-b, --breakdown', 'Show per-model cost breakdown')
  .option('-O, --offline', 'Use cached pricing data for Claude models instead of fetching from API')
  .option('--no-offline', 'Use online pricing data')
  .option('--color', 'Enable colored output (default: auto). FORCE_COLOR=1 has the same effect.')
  .option('--no-color', 'Disable colored output (default: auto). NO_COLOR=1 has the same effect.')
  .option('-i, --instances', 'Show usage breakdown by project/instance')
  .option('-p, --project <project>', 'Filter to specific project name')
  .action(async () => {
    const args = process.argv.slice(3) // Remove 'vibetime', 'monthly'
    const result = await executeCcusage(['monthly', ...args])
    process.exit(result.exitCode)
  })

program
  .command('session')
  .description('View session-based Claude usage report (ccusage compatible)')
  .option('-s, --since <since>', 'Filter from date (YYYYMMDD format)')
  .option('-u, --until <until>', 'Filter until date (YYYYMMDD format)')
  .option('-j, --json', 'Output in JSON format')
  .option('-m, --mode <mode>', 'Cost calculation mode: auto (use costUSD if exists, otherwise calculate), calculate (always calculate), display (always use costUSD)', 'auto')
  .option('-d, --debug', 'Show pricing mismatch information for debugging')
  .option('--debug-samples <samples>', 'Number of sample discrepancies to show in debug output', '5')
  .option('-o, --order <order>', 'Sort order: desc (newest first) or asc (oldest first)', 'asc')
  .option('-b, --breakdown', 'Show per-model cost breakdown')
  .option('-O, --offline', 'Use cached pricing data for Claude models instead of fetching from API')
  .option('--no-offline', 'Use online pricing data')
  .option('--color', 'Enable colored output (default: auto). FORCE_COLOR=1 has the same effect.')
  .option('--no-color', 'Disable colored output (default: auto). NO_COLOR=1 has the same effect.')
  .action(async () => {
    const args = process.argv.slice(3) // Remove 'vibetime', 'session'
    const result = await executeCcusage(['session', ...args])
    process.exit(result.exitCode)
  })

program
  .command('blocks')
  .description('View 5-hour block usage report (ccusage compatible)')
  .option('-s, --since <since>', 'Filter from date (YYYYMMDD format)')
  .option('-u, --until <until>', 'Filter until date (YYYYMMDD format)')
  .option('-j, --json', 'Output in JSON format')
  .option('-m, --mode <mode>', 'Cost calculation mode: auto (use costUSD if exists, otherwise calculate), calculate (always calculate), display (always use costUSD)', 'auto')
  .option('-d, --debug', 'Show pricing mismatch information for debugging')
  .option('--debug-samples <samples>', 'Number of sample discrepancies to show in debug output', '5')
  .option('-o, --order <order>', 'Sort order: desc (newest first) or asc (oldest first)', 'asc')
  .option('-b, --breakdown', 'Show per-model cost breakdown')
  .option('-O, --offline', 'Use cached pricing data for Claude models instead of fetching from API')
  .option('--no-offline', 'Use online pricing data')
  .option('--color', 'Enable colored output (default: auto). FORCE_COLOR=1 has the same effect.')
  .option('--no-color', 'Disable colored output (default: auto). NO_COLOR=1 has the same effect.')
  .option('-a, --active', 'Show only active block with projections')
  .option('-r, --recent', 'Show blocks from last 3 days (including active)')
  .option('-t, --token-limit <limit>', 'Token limit for quota warnings (e.g., 500000 or "max")')
  .option('-l, --session-length <hours>', 'Session block duration in hours', '5')
  .option('--live', 'Live monitoring mode with real-time updates')
  .option('--refresh-interval <seconds>', 'Refresh interval in seconds for live mode', '1')
  .action(async () => {
    const args = process.argv.slice(3) // Remove 'vibetime', 'blocks'
    const result = await executeCcusage(['blocks', ...args])
    process.exit(result.exitCode)
  })

// Add extended vibetime commands
program.addCommand(configCommand)
program.addCommand(testCommand)
program.addCommand(syncCommand)

// Check API key status and show welcome message if not configured
async function checkApiKeyStatus() {
  try {
    await getApiKey()
  } catch {
    console.log(pc.cyan('\n🎉 Welcome to VibeTime CLI!\n'))
    console.log(pc.white('VibeTime = ccusage + cloud sync'))
    console.log(pc.gray('All ccusage commands work immediately:\n'))
    console.log(pc.green('  vibetime daily') + pc.gray('    - View daily usage report'))
    console.log(pc.green('  vibetime monthly') + pc.gray('  - View monthly usage report'))
    console.log(pc.green('  vibetime session') + pc.gray('  - View session usage report'))
    console.log(pc.green('  vibetime blocks') + pc.gray('   - View 5-hour block usage report'))
    console.log(pc.cyan('\n☁️  Want to sync to cloud dashboard?'))
    console.log(pc.green('  vibetime config set') + pc.gray(' - Set up your API key'))
    console.log(pc.green('  vibetime sync') + pc.gray('      - Upload your usage data'))
    console.log(pc.yellow('\n📌 Get your FREE API key at:'))
    console.log(pc.blue('   https://vibetime.ekusiadadus.workers.dev'))
    console.log(pc.gray('\nNote: ccusage commands work without an API key\n'))
  }
}

// Global error handling
program.exitOverride()

try {
  // Check API key status on first run
  if (process.argv.length === 2) {
    await checkApiKeyStatus()
  }
  
  await program.parseAsync(process.argv)
} catch (error: any) {
  if (error.code === 'commander.helpDisplayed' || error.code === 'commander.version') {
    process.exit(0)
  }
  console.error(pc.red('Error:'), error.message)
  process.exit(1)
}